import { IconHelp, IconPlus, IconTrash } from '@tabler/icons-react';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Button } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Divider } from '@zorro/design-system/v2/Divider';
import { SsnPasswordInput } from '@zorro/design-system/v2/SsnPasswordInput';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  AddressInput,
  Box,
  Center,
  DateInput,
  FormErrorMessage,
  Grid,
  Group,
  Icon,
  PhoneInput,
  Stack,
  Text,
  brand,
} from '@zorro/web/design-system';
import { getAdultDefaultDate, useRoles } from '@zorro/web/ui-utils';
import { Fragment, ReactElement } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form';

import { PersonFormItem, getDefaultPersonFormItem } from './PersonFormUtils';

type Props<T extends FieldValues> = {
  icon: ReactElement;
  title: string;
  subtitle: string;
  personTitle?: string;
  tooltipLabel?: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  fieldName: string;
  activeName: string;
  fields: PersonFormItem[];
  append: (value: PersonFormItem | PersonFormItem[]) => void;
  remove: (index: number) => void;
  isDuplicateMode?: boolean;
  isFinalizationMode?: boolean;
  shouldShowIdentityFields?: boolean;
  shouldHideMakeAdmin?: boolean;
  isAdminFixed?: boolean;
  duplicateModeCheckbox?: { label: string };
  isMultiSelect?: boolean;
  maxItems?: number;
};

export const PersonFormArray = <T extends FieldValues>({
  icon,
  title,
  subtitle,
  personTitle,
  tooltipLabel,
  control,
  errors,
  fieldName,
  activeName,
  fields,
  append,
  remove,
  isDuplicateMode = false,
  isFinalizationMode,
  shouldShowIdentityFields = false,
  shouldHideMakeAdmin = false,
  isAdminFixed = false,
  duplicateModeCheckbox,
  isMultiSelect = false,
  maxItems,
}: Props<T>) => {
  'use no memo';
  const { isAgentWithoutSupervisor: isReadonlyMode } = useRoles();

  return (
    <Card
      variant="white-border"
      bd="1px solid var(--mantine-color-zorroGray-1)"
      radius="md"
      p={0}
    >
      <Group
        align="start"
        bg="zorroGray.10"
        wrap="nowrap"
        py="lg"
        px="xl"
        gap={8}
      >
        {icon}
        <Stack gap={2}>
          <Group gap="xs">
            <Text fw={600}>
              {title} {isFinalizationMode ? '*' : ''}
            </Text>
            {tooltipLabel && (
              <Tooltip label={tooltipLabel} position="top">
                <Box pos="relative">
                  <Center>
                    <Icon icon={IconHelp} size="1.25rem" />
                  </Center>
                </Box>
              </Tooltip>
            )}
          </Group>
          <Text c="zorroGray.9" size="sm">
            {subtitle}
          </Text>
        </Stack>
      </Group>

      <Stack key={isDuplicateMode.toString()} py="lg" px="xl">
        {fields.map((_, index) => {
          const shouldShowRequired = isFinalizationMode;
          const showDuplicateCheckbox = duplicateModeCheckbox && index === 0;
          const shouldShowAddButton =
            isMultiSelect &&
            index === fields.length - 1 &&
            !isDuplicateMode &&
            (maxItems === undefined || fields.length < maxItems);

          return (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Fragment key={index.toString()}>
              <Group justify="space-between" wrap="nowrap" w="100%">
                {personTitle && (
                  <Text fw="600" style={{ whiteSpace: 'nowrap' }}>
                    {personTitle} {index + 1}
                  </Text>
                )}
                {index > 0 && (
                  <>
                    <Divider w="100%" />
                    <ActionIcon onClick={() => remove(index)} size="md">
                      <Icon
                        icon={IconTrash}
                        color={brand.zorroGray800}
                        size={20}
                      />
                    </ActionIcon>
                  </>
                )}
                {showDuplicateCheckbox && (
                  <Controller
                    control={control}
                    name={`${fieldName}.${index}.isDuplicateMode` as Path<T>}
                    render={({ field: { value, ...rest } }) => (
                      <Checkbox
                        {...rest}
                        checked={value || false}
                        label={duplicateModeCheckbox.label}
                        ml="auto"
                      />
                    )}
                  />
                )}
              </Group>

              <Grid>
                <Grid.Col span={6}>
                  <Controller
                    control={control}
                    name={`${activeName}.${index}.firstName` as Path<T>}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        disabled={isDuplicateMode}
                        isReadOnly={isReadonlyMode}
                        required={shouldShowRequired}
                        placeholder={isReadonlyMode ? '-' : 'Type first name'}
                        label="First name"
                        size="md"
                      />
                    )}
                  />
                  <FormErrorMessage
                    fieldName={`${activeName}.${index}.firstName` as Path<T>}
                    errors={errors}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Controller
                    control={control}
                    name={`${activeName}.${index}.lastName` as Path<T>}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        disabled={isDuplicateMode}
                        isReadOnly={isReadonlyMode}
                        required={shouldShowRequired}
                        placeholder={isReadonlyMode ? '-' : 'Type last name'}
                        label="Last name"
                        size="md"
                      />
                    )}
                  />
                  <FormErrorMessage
                    fieldName={`${activeName}.${index}.lastName` as Path<T>}
                    errors={errors}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Controller
                    control={control}
                    name={`${activeName}.${index}.phone` as Path<T>}
                    render={({ field }) => (
                      <PhoneInput
                        {...field}
                        disabled={isDuplicateMode}
                        isReadOnly={isReadonlyMode}
                        isRequired={shouldShowRequired}
                        placeholder="(212) 456-7890"
                        international={false}
                        label="Phone number"
                        size="md"
                      />
                    )}
                  />
                  <FormErrorMessage
                    fieldName={`${activeName}.${index}.phone` as Path<T>}
                    errors={errors}
                  />
                </Grid.Col>

                <Grid.Col span={6}>
                  <Controller
                    control={control}
                    name={`${activeName}.${index}.email` as Path<T>}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        disabled={isDuplicateMode}
                        isReadOnly={isReadonlyMode}
                        required={shouldShowRequired}
                        placeholder={
                          isReadonlyMode ? '-' : 'Type email address'
                        }
                        label="Email address"
                        size="md"
                      />
                    )}
                  />
                  <FormErrorMessage
                    fieldName={`${activeName}.${index}.email` as Path<T>}
                    errors={errors}
                  />
                </Grid.Col>

                {shouldShowIdentityFields && (
                  <>
                    <Grid.Col span={12}>
                      <Controller
                        control={control}
                        name={`${activeName}.${index}.address` as Path<T>}
                        render={({ field: { onChange, ...rest } }) => (
                          <AddressInput
                            {...rest}
                            disabled={isDuplicateMode}
                            isReadOnly={isReadonlyMode}
                            required={shouldShowRequired}
                            notifySubscribers={(address: string) =>
                              onChange(address)
                            }
                            onChange={onChange}
                            placeholder="Type the home address"
                            label="Home address"
                            size="md"
                          />
                        )}
                      />
                      <FormErrorMessage
                        fieldName={`${activeName}.${index}.address` as Path<T>}
                        errors={errors}
                      />
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <Controller
                        control={control}
                        name={`${activeName}.${index}.dateOfBirth` as Path<T>}
                        render={({ field }) => (
                          <DateInput
                            {...field}
                            disabled={isDuplicateMode}
                            isReadOnly={isReadonlyMode}
                            required={shouldShowRequired}
                            label="Date of birth"
                            defaultLevel="decade"
                            defaultDate={getAdultDefaultDate()}
                            size="md"
                          />
                        )}
                      />
                      <FormErrorMessage
                        fieldName={
                          `${activeName}.${index}.dateOfBirth` as Path<T>
                        }
                        errors={errors}
                      />
                    </Grid.Col>

                    <Grid.Col span={6}>
                      <Controller
                        control={control}
                        name={`${activeName}.${index}.ssn` as Path<T>}
                        render={({ field }) => (
                          <SsnPasswordInput
                            {...field}
                            disabled={isDuplicateMode}
                            isReadOnly={isReadonlyMode}
                            required={shouldShowRequired}
                            label="SSN"
                            size="md"
                            isMasked
                          />
                        )}
                      />
                      <FormErrorMessage
                        fieldName={`${activeName}.${index}.ssn` as Path<T>}
                        errors={errors}
                      />
                    </Grid.Col>
                  </>
                )}

                <Grid.Col mb="md">
                  <Group justify="space-between">
                    {!shouldHideMakeAdmin && (
                      <Controller
                        control={control}
                        name={`${activeName}.${index}.isAdmin` as Path<T>}
                        render={({ field: { value, ...rest } }) => (
                          <Checkbox
                            {...rest}
                            disabled={isDuplicateMode}
                            isReadOnly={isAdminFixed}
                            checked={isAdminFixed ? true : value || false}
                            label="Make a Zorro admin"
                          />
                        )}
                      />
                    )}
                    <FormErrorMessage
                      fieldName={`${activeName}.${index}.isAdmin` as Path<T>}
                      errors={errors}
                    />
                    {shouldShowAddButton && (
                      <Button
                        onClick={() =>
                          append(
                            getDefaultPersonFormItem(
                              isAdminFixed,
                              shouldShowIdentityFields
                            )
                          )
                        }
                        disabled={isReadonlyMode}
                        variant="primary"
                        size="xs"
                        leftSection={<IconPlus />}
                        aria-label={`Add ${personTitle ?? 'person'}`}
                      />
                    )}
                  </Group>
                </Grid.Col>
              </Grid>
            </Fragment>
          );
        })}
      </Stack>
    </Card>
  );
};
